import React from 'react';
import { Button, Card, Divider, Flex, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

import { AiCrawlerLineup, MISSING_CRAWLERS_TOOLTIP, useAiCrawlerLineup } from '../../shared';
import type { DiscoverabilityInspectPagesTab } from '../DiscoverabilityAnalyticsEvents';
import {
  isEvaluableDomain,
  type DiscoverabilityStatusResponse,
  type DiscoverabilityStatusTrigger,
} from '../api/discoverabilityStatusApi';

type DiscoverabilityStatusBannerProps = {
  status: DiscoverabilityStatusResponse;
  onDismiss: () => void;
  onGoToDiagnostics: () => void;
  onInspectPages: (tab: DiscoverabilityInspectPagesTab) => void;
  /**
   * Renders the findings without any control the reader cannot act on — no
   * dismiss, no CTAs. Used by the PDF export, where the banner is the headline
   * result but every action would be dead ink.
   */
  readOnly?: boolean;
};

type DiscoverabilityActionableStatus = Exclude<
  DiscoverabilityStatusResponse['status'],
  'no_data'
>;

const TITLES: Record<DiscoverabilityActionableStatus, string> = {
  critical: 'Your Discoverability has critical issues',
  needs_improvement: 'Your Discoverability needs improvement',
  healthy: 'Your Discoverability is healthy',
};

const SUBTITLES: Record<DiscoverabilityActionableStatus, string> = {
  critical: 'Updates daily based on the last 7 days of AI Crawler activity.',
  needs_improvement: 'Updates daily based on the last 7 days of AI Crawler activity.',
  healthy: 'All tracked AI Crawlers are reaching your pages.',
};

const NO_DATA_MESSAGE =
  'We assess your Discoverability based on 7 days of AI Crawler activity. Check back soon.';

const TRIGGER_ORDER: DiscoverabilityStatusTrigger[] = [
  'missing_crawlers',
  'access_blocked_pages',
  'access_limited_pages',
];

// The severity tier each trigger belongs to, mirroring rest-api's
// `AIDiscoverabilityStatus` (`isCritical` = missing crawlers or blocked pages;
// `needsImprovement` = limited-access pages). The API's `triggers` array lists
// every finding regardless of tier, so the banner scopes the rows it renders to
// the tier it announces — a "critical issues" heading must not be followed by
// warning-tier findings (PRE-3215). Keep in sync when a trigger is added.
const TRIGGER_TIER: Record<
  DiscoverabilityStatusTrigger,
  DiscoverabilityActionableStatus
> = {
  missing_crawlers: 'critical',
  access_blocked_pages: 'critical',
  access_limited_pages: 'needs_improvement',
};

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function sumBy<T>(items: T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}

function healthyStatusTooltip(lineup: AiCrawlerLineup): string {
  return `All ${lineup.providers.length} AI Crawlers detected in the last 7 days. No blocked or access limited pages.`;
}

const SCAN_SNAPSHOT_TOOLTIP =
  'Counts reflect the latest daily scan. Live data updates continuously, so numbers elsewhere on the page may vary slightly.';

const INSPECT_PAGES_TOOLTIP = "View pages AI crawlers couldn't access in the last 7 days";

/**
 * The banner's tooltip copy for a given status — only the tooltips the banner
 * actually renders for it, in the order their controls appear.
 *
 * Exported for the PDF export, which prints tooltip copy as body text because
 * nothing can be hovered on paper. The conditions live here, beside the
 * rendering they mirror, so the export can't end up explaining a finding the
 * banner didn't show.
 *
 * Copy only, no headings: unlike the KPI and chart tooltips, these hang off
 * dynamic banner text with no on-screen title to reuse, and the export lists
 * them under the banner they explain rather than inventing labels.
 */
export function bannerExplanations(lineup: AiCrawlerLineup, status: DiscoverabilityStatusResponse): string[] {
  const explanations: string[] = [];

  if (status.status === 'healthy') {
    explanations.push(healthyStatusTooltip(lineup));
  }

  if (status.status === 'critical' || status.status === 'needs_improvement') {
    explanations.push(SCAN_SNAPSHOT_TOOLTIP);
  }

  if (
    status.triggers.includes('missing_crawlers') &&
    TRIGGER_TIER.missing_crawlers === status.status
  ) {
    explanations.push(MISSING_CRAWLERS_TOOLTIP);
  }

  return explanations;
}

function buildMissingCrawlersContent(status: DiscoverabilityStatusResponse): React.ReactNode {
  const evaluable = status.domains.filter(isEvaluableDomain);
  const affectedDomains = evaluable.filter((d) => d.missingCrawlers.length > 0);

  if (affectedDomains.length === 1) {
    return (
      <>
        {"Some AI Crawlers aren't reaching "}
        <Typography.Text strong>{affectedDomains[0].domain}</Typography.Text>
        {'.'}
      </>
    );
  }

  return (
    <>
      {"Some AI Crawlers aren't reaching all your domains. Gaps found across "}
      <Typography.Text strong>{`${affectedDomains.length} domains`}</Typography.Text>
      {'.'}
    </>
  );
}

function buildAccessBlockedPagesContent(status: DiscoverabilityStatusResponse): React.ReactNode {
  const evaluable = status.domains.filter(isEvaluableDomain);
  const total = sumBy(evaluable, (d) => d.accessBlockedPageCount);

  return (
    <>
      <Typography.Text strong>{`${total} ${pluralize(total, 'page', 'pages')}`}</Typography.Text>
      {' '}
      <Typography.Text strong>blocked</Typography.Text>
      {' for AI Crawlers'}
    </>
  );
}

function buildAccessLimitedPagesContent(status: DiscoverabilityStatusResponse): React.ReactNode {
  const evaluable = status.domains.filter(isEvaluableDomain);
  const total = sumBy(evaluable, (d) => d.accessLimitedPageCount);

  return (
    <>
      <Typography.Text strong>{`${total} ${pluralize(total, 'page', 'pages')}`}</Typography.Text>
      {' '}
      <Typography.Text strong>with limited access</Typography.Text>
      {' for AI Crawlers'}
    </>
  );
}

type TriggerLineConfig = {
  trigger: DiscoverabilityStatusTrigger;
  icon: React.ReactNode;
  content: React.ReactNode;
  tooltip?: string;
  cta: React.ReactNode;
};

type CtaProps = {
  label: string;
  onClick: () => void;
  linkColor: string;
  tooltip?: string;
};

function CtaButton({ label, onClick, linkColor, tooltip }: CtaProps) {
  const button = (
    <Button
      type="link"
      icon={<ArrowRightOutlined />}
      onClick={onClick}
      style={{ color: linkColor, padding: 0 }}
    >
      {label}
    </Button>
  );

  if (!tooltip) return button;

  return (
    <CustomTooltip placement="top" message={tooltip}>
      {button}
    </CustomTooltip>
  );
}

type BannerHeaderProps = {
  status: DiscoverabilityStatusResponse['status'];
  onDismiss: () => void;
  readOnly?: boolean;
};

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  const { token } = theme.useToken();

  return (
    <Button
      type="text"
      icon={<CloseOutlined />}
      onClick={onDismiss}
      style={{ color: token.colorTextSecondary, padding: `0 ${token.marginXXS}px` }}
    >
      Dismiss
    </Button>
  );
}

function BannerHeader({ status, onDismiss, readOnly }: BannerHeaderProps) {
  const lineup = useAiCrawlerLineup();
  const { token } = theme.useToken();

  if (status === 'no_data') {
    return (
      <Flex align="center" gap={token.marginSM}>
        <Typography.Text style={{ flex: 1, fontSize: token.fontSizeLG }}>
          {NO_DATA_MESSAGE}
        </Typography.Text>
        {!readOnly && <DismissButton onDismiss={onDismiss} />}
      </Flex>
    );
  }

  return (
    <Flex align="flex-start" gap={token.marginSM}>
      <Flex vertical flex={1}>
        <Flex align="center" gap={token.marginXS}>
          <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
            {TITLES[status]}
          </Typography.Text>
          {status === 'healthy' && (
            <CustomTooltip placement="top" message={healthyStatusTooltip(lineup)}>
              <InfoCircleOutlined
                style={{
                  fontSize: token.fontSize,
                  color: token.colorTextSecondary,
                  cursor: 'pointer',
                }}
              />
            </CustomTooltip>
          )}
        </Flex>
        <Flex align="center" gap={token.marginXS}>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {SUBTITLES[status]}
          </Typography.Text>
          {(status === 'critical' || status === 'needs_improvement') && (
            <CustomTooltip placement="top" message={SCAN_SNAPSHOT_TOOLTIP}>
              <InfoCircleOutlined
                style={{
                  fontSize: token.fontSize,
                  color: token.colorTextSecondary,
                  cursor: 'pointer',
                }}
              />
            </CustomTooltip>
          )}
        </Flex>
      </Flex>
      {!readOnly && <DismissButton onDismiss={onDismiss} />}
    </Flex>
  );
}

type TriggerLineProps = {
  icon: React.ReactNode;
  content: React.ReactNode;
  tooltip?: string;
  cta: React.ReactNode;
};

function TriggerLine({ icon, content, tooltip, cta }: TriggerLineProps) {
  const { token } = theme.useToken();

  return (
    <>
      <Divider style={{ margin: 0 }} />
      <Flex align="center" gap={token.marginXS}>
        {icon}
        <Typography.Text style={{ fontSize: token.fontSize }}>{content}</Typography.Text>
        {tooltip && (
          <CustomTooltip placement="top" message={tooltip}>
            <InfoCircleOutlined
              style={{
                fontSize: token.fontSize,
                color: token.colorTextSecondary,
                cursor: 'pointer',
              }}
            />
          </CustomTooltip>
        )}
        <Flex flex={1} justify="flex-end">
          {cta}
        </Flex>
      </Flex>
    </>
  );
}

export default function DiscoverabilityStatusBanner({
  status,
  onDismiss,
  onGoToDiagnostics,
  onInspectPages,
  readOnly = false,
}: DiscoverabilityStatusBannerProps) {
  const { token } = theme.useToken();

  const triggerLines = ((): TriggerLineConfig[] => {
    if (status.status === 'healthy' || status.status === 'no_data') {
      return [];
    }

    const iconStyle = { color: token.colorWarning, fontSize: token.fontSizeLG };

    const builders: Record<DiscoverabilityStatusTrigger, () => TriggerLineConfig> = {
      missing_crawlers: () => ({
        trigger: 'missing_crawlers',
        icon: <WarningOutlined style={iconStyle} />,
        content: buildMissingCrawlersContent(status),
        tooltip: MISSING_CRAWLERS_TOOLTIP,
        cta: readOnly ? null : (
          <CtaButton
            label="Go to Diagnostics"
            linkColor={token.colorPrimary}
            onClick={onGoToDiagnostics}
          />
        ),
      }),
      access_blocked_pages: () => ({
        trigger: 'access_blocked_pages',
        icon: <StopOutlined style={iconStyle} />,
        content: buildAccessBlockedPagesContent(status),
        cta: readOnly ? null : (
          <CtaButton
            label="Inspect pages"
            linkColor={token.colorPrimary}
            tooltip={INSPECT_PAGES_TOOLTIP}
            onClick={() => onInspectPages('access_blocked')}
          />
        ),
      }),
      access_limited_pages: () => ({
        trigger: 'access_limited_pages',
        icon: <WarningOutlined style={iconStyle} />,
        content: buildAccessLimitedPagesContent(status),
        cta: readOnly ? null : (
          <CtaButton
            label="Inspect pages"
            linkColor={token.colorPrimary}
            tooltip={INSPECT_PAGES_TOOLTIP}
            onClick={() => onInspectPages('access_limited')}
          />
        ),
      }),
    };

    const announcedTier = status.status;

    return TRIGGER_ORDER.filter(
      (trigger) =>
        status.triggers.includes(trigger) && TRIGGER_TIER[trigger] === announcedTier
    ).map((trigger) => builders[trigger]());
  })();

  return (
    <Card styles={{ body: { padding: token.padding } }}>
      <Flex vertical gap={token.marginSM}>
        <BannerHeader status={status.status} onDismiss={onDismiss} readOnly={readOnly} />
        {triggerLines.map((line) => (
          <TriggerLine
            key={line.trigger}
            icon={line.icon}
            content={line.content}
            tooltip={line.tooltip}
            cta={line.cta}
          />
        ))}
      </Flex>
    </Card>
  );
}
